import Tooltip from '@mui/material/Tooltip'

import {
    COP_SITE_DETAILS_ACCESSIBILITY,
    COP_SITE_DETAILS_BUTTON_TYPE,
    COP_SITE_DETAILS_EXPORT,
} from '../constants/copSiteDetails.constants'
import { ButtonConfig } from '../copSiteDetails'
import { ChevronIcon } from './SiteDetailsIcons'

// ─── Types ────────────────────────────────────────────────────────────────────

type SiteDetailsActionBarProps = {
    activeButtons: ButtonConfig[]
    ctaButtons: ButtonConfig[]
    exportLabel: string
    dropdownOpen: boolean
    isExporting: boolean
    isExportDisabled: boolean
    onToggleDropdown: () => void
    onExportExcel: () => void
    onExportPDF: () => void
    onMobileExportExcel: (e: React.MouseEvent) => void
    onMobileExportPDF: (e: React.MouseEvent) => void
    onReportIssueClick: () => void
    onViewAssetsTree: () => void
    assetsTreeDisabled: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SiteDetailsActionBar({
    activeButtons,
    ctaButtons,
    exportLabel,
    dropdownOpen,
    isExporting,
    isExportDisabled,
    onToggleDropdown,
    onExportExcel,
    onExportPDF,
    onMobileExportExcel,
    onMobileExportPDF,
    onReportIssueClick,
    onViewAssetsTree,
    assetsTreeDisabled,
}: Readonly<SiteDetailsActionBarProps>) {
    const desktopExportMenuId = 'cop-site-details-export-menu'
    const mobileExportMenuId = 'cop-site-details-export-menu-mobile'

    const isPopupTriggerUrl = (url?: string): boolean => {
        const normalized = (url || '').trim()
        return normalized === '' || normalized === '#' || normalized.startsWith('#')
    }

    const isAuthoredReportIcon = (btn: ButtonConfig): boolean => {
        const icon = (btn.buttonIcon || '').toLowerCase()
        return btn.buttonType === COP_SITE_DETAILS_BUTTON_TYPE.CTA && icon.includes('alert')
    }

    const isAssetsTreeIcon = (btn: ButtonConfig): boolean => (btn.buttonIcon || '').toLowerCase().includes('vector')

    const handleAuthoredButtonClick = (e: React.MouseEvent<HTMLAnchorElement>, btn: ButtonConfig) => {
        if (isAssetsTreeIcon(btn)) {
            e.preventDefault()
            if (!assetsTreeDisabled) onViewAssetsTree()
            return
        }
        if (!isPopupTriggerUrl(btn.buttonUrl) && !isAuthoredReportIcon(btn)) {
            return
        }
        e.preventDefault()
        onReportIssueClick()
    }

    const renderActionIcon = (btn: ButtonConfig, key: string): JSX.Element => {
        const disabled = isAssetsTreeIcon(btn) && assetsTreeDisabled
        const icon = (
            <a
                href={btn.buttonUrl || '#'}
                className={`icon-btn${disabled ? ' icon-btn--disabled' : ''}`}
                aria-label={btn.buttonLabel || btn.buttonIcon}
                aria-disabled={disabled}
                onClick={e => handleAuthoredButtonClick(e, btn)}
            >
                {btn.buttonIcon && <span className={`icon icon-${btn.buttonIcon}`} aria-hidden="true" />}
            </a>
        )

        if (!disabled || !btn.buttonTooltip) return <span key={key}>{icon}</span>

        return (
            <Tooltip
                key={key}
                title={btn.buttonTooltip}
                placement="top"
                arrow
                slotProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#fff',
                            color: '#1a202c',
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: '8px 12px',
                            borderRadius: '4px',
                            boxShadow: '0 1px 6px rgb(0 0 0 / 30%)',
                            whiteSpace: 'nowrap',
                            maxWidth: 'none',
                        },
                    },
                    arrow: { sx: { color: '#fff' } },
                }}
            >
                {icon}
            </Tooltip>
        )
    }

    return (
        <>
            {/* Desktop action bar (hidden on mobile via CSS) */}
            <div className="actions">

                {activeButtons.map((btn, idx) => {
                    // Skip CTA buttons that have neither a label nor an icon (truly empty)
                    if (btn.buttonType === COP_SITE_DETAILS_BUTTON_TYPE.CTA && !btn.buttonLabel?.trim() && !btn.buttonIcon?.trim()) {
                        return null
                    }

                    if (btn.buttonType === COP_SITE_DETAILS_BUTTON_TYPE.DROPDOWN) {
                        return (
                            <div key={`${btn.buttonType}-${btn.buttonLabel || 'export'}`} className="export-wrapper">
                                <button
                                    type="button"
                                    className="export-btn"
                                    onClick={onToggleDropdown}
                                    disabled={isExportDisabled}
                                    aria-haspopup="menu"
                                    aria-expanded={dropdownOpen}
                                    aria-controls={desktopExportMenuId}
                                    aria-label={COP_SITE_DETAILS_ACCESSIBILITY.EXPORT_ARIA_LABEL}
                                >
                                    <span>{btn.buttonLabel || exportLabel}</span>
                                    <ChevronIcon open={dropdownOpen} />
                                </button>
                                {dropdownOpen && (
                                    <div
                                        id={desktopExportMenuId}
                                        className="export-menu"
                                        role="menu"
                                        aria-label={COP_SITE_DETAILS_ACCESSIBILITY.EXPORT_MENU_ARIA_LABEL}
                                    >
                                        <button
                                            type="button"
                                            className="export-item"
                                            role="menuitem"
                                            onClick={onExportExcel}
                                            disabled={isExporting}
                                        >
                                            {COP_SITE_DETAILS_EXPORT.EXCEL}
                                        </button>
                                        <button
                                            type="button"
                                            className="export-item"
                                            role="menuitem"
                                            onClick={onExportPDF}
                                            disabled={isExporting}
                                        >
                                            {COP_SITE_DETAILS_EXPORT.PDF}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    return renderActionIcon(btn, `${btn.buttonType}-${btn.buttonLabel || btn.buttonIcon || idx}`)
                })}
            </div>

            {/* Mobile actions: authored CTA icons + export menu (hidden on desktop via CSS) */}
            <div className="mobile-actions">

                {/* CTA icon buttons — shown directly beside the kebab */}
                {ctaButtons.map((btn) =>
                    btn.buttonIcon
                        ? renderActionIcon(btn, `${btn.buttonType}-${btn.buttonLabel || btn.buttonIcon}`)
                        : null
                )}

                {/* Export kebab button */}
                <button
                    type="button"
                    className="menu-btn"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={dropdownOpen}
                    aria-controls={mobileExportMenuId}
                    onClick={onToggleDropdown}
                >
                    &#8942;
                    {dropdownOpen && (
                        <div
                            id={mobileExportMenuId}
                            className="export-menu export-menu--mobile"
                            role="menu"
                            aria-label={COP_SITE_DETAILS_ACCESSIBILITY.EXPORT_MENU_ARIA_LABEL}
                        >
                            <button
                                type="button"
                                className="export-item"
                                role="menuitem"
                                onClick={onMobileExportExcel}
                                disabled={isExporting}
                            >
                                {COP_SITE_DETAILS_EXPORT.EXCEL}
                            </button>
                            <button
                                type="button"
                                className="export-item"
                                role="menuitem"
                                onClick={onMobileExportPDF}
                                disabled={isExporting}
                            >
                                {COP_SITE_DETAILS_EXPORT.PDF}
                            </button>
                        </div>
                    )}
                </button>

            </div>
        </>
    )
}